import { PlannedPlanSettings } from "./remaining-settings-sections";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpdateBillingSettings } from "@zworks/console-contracts";
import * as React from "react";
import { ApiError, billingQuery, updateBilling } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EmptyState, ErrorState, LoadingState, NotConnectedState } from "@/components/states";
import { formatCentsUsd, formatCredits, formatDate } from "@/lib/format";

export function BillingSection({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const billing = useQuery(billingQuery(projectId));
  const [draft, setDraft] = React.useState<UpdateBillingSettings | null>(null);
  const [draftProjectId, setDraftProjectId] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (settings: UpdateBillingSettings) => updateBilling(projectId, settings),
    onSuccess: async (updated) => {
      setDraft(null);
      setNotice("Billing settings saved.");
      await queryClient.setQueryData(billingQuery(projectId).queryKey, updated);
    },
  });

  if (billing.isPending) return <LoadingState label="Loading billing settings" rows={3} />;
  if (billing.isError) {
    return (
      <ErrorState
        title="Billing could not be loaded"
        description={
          billing.error instanceof ApiError
            ? (billing.error.problem?.detail ?? billing.error.message)
            : "The request could not be completed."
        }
        requestId={
          billing.error instanceof ApiError ? (billing.error.requestId ?? "unknown") : "unknown"
        }
        onRetry={() => void billing.refetch()}
      />
    );
  }

  const value = billing.data;
  const invoices = value.invoices;
  const form =
    draftProjectId === projectId && draft
      ? draft
      : {
          monthlyLimit: value.monthlyLimit,
          perRunCap: value.perRunCap,
          autoRecharge: value.autoRecharge,
          billingEmail: value.billingEmail,
          taxId: value.taxId,
          taxRate: value.taxRate,
        };
  const held = value.reservedBalance;
  const hasChanged =
    form.monthlyLimit !== value.monthlyLimit ||
    form.perRunCap !== value.perRunCap ||
    form.billingEmail !== value.billingEmail ||
    form.taxId !== value.taxId ||
    form.taxRate !== value.taxRate ||
    JSON.stringify(form.autoRecharge) !== JSON.stringify(value.autoRecharge);

  function edit(patch: Partial<UpdateBillingSettings>) {
    setDraft({ ...form, ...patch });
    setDraftProjectId(projectId);
    setNotice(null);
  }

  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate(form);
  }

  return (
    <form
      aria-label="Billing settings"
      onSubmit={(event) => void save(event)}
      className="space-y-8"
    >
      <PlannedPlanSettings />
      <section aria-labelledby="billing-credits-title">
        <h2 id="billing-credits-title" className="text-lg font-semibold tracking-tight">
          Credits
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Card>
            <CardContent className="flex items-center justify-between gap-4 pt-6">
              <div>
                <p className="text-sm text-muted-foreground">Available balance</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {formatCredits(value.availableBalance)}
                </p>
              </div>
              <Badge variant="secondary">Shared</Badge>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center justify-between gap-4 pt-6">
              <div>
                <p className="text-sm text-muted-foreground">Held for running runs</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {formatCredits(held)}{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    held for running runs
                  </span>
                </p>
              </div>
              <Badge variant="secondary">Reserved</Badge>
            </CardContent>
          </Card>
        </div>
      </section>

      <section aria-labelledby="billing-recharge-title" className="space-y-3">
        <div>
          <h2 id="billing-recharge-title" className="text-lg font-semibold tracking-tight">
            Auto-recharge
          </h2>
          <p className="text-sm text-muted-foreground">
            {value.livePayments
              ? "Buy a credit tier when the available balance falls below your threshold. Off by default."
              : "Buy a credit tier when the available balance falls below your threshold. Not available yet: auto-recharge needs a live payment provider."}
          </p>
        </div>
        <NotConnectedState
          title="Auto-recharge is not connected yet"
          description="It needs a saved payment method from an active payment provider. Until then, top up your balance manually."
        />
      </section>

      <section aria-labelledby="billing-limit-title" className="space-y-3">
        <div>
          <h2 id="billing-limit-title" className="text-lg font-semibold tracking-tight">
            Monthly spending limit
          </h2>
          <p className="text-sm text-muted-foreground">
            New runs pause when this limit is reached. Runs already in progress continue.
          </p>
        </div>
        <label
          htmlFor="monthly-limit"
          className="flex flex-wrap items-center gap-3 text-sm font-medium"
        >
          Monthly limit
          <Input
            id="monthly-limit"
            aria-label="Monthly spending limit"
            type="number"
            min={0}
            step={1}
            className="w-36 tabular-nums"
            value={form.monthlyLimit}
            disabled={mutation.isPending}
            onChange={(event) => edit({ monthlyLimit: Number(event.target.value) })}
          />
          <span className="text-muted-foreground">credits</span>
        </label>
      </section>

      <section aria-labelledby="billing-method-title" className="space-y-3">
        <h2 id="billing-method-title" className="text-lg font-semibold tracking-tight">
          Payment method
        </h2>
        {value.paymentMethod ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
            <div>
              <p className="font-medium">
                {value.paymentMethod.brand} ending {value.paymentMethod.lastFour}
              </p>
              <p className="text-sm text-muted-foreground">Expires {value.paymentMethod.expiry}</p>
            </div>
            <Badge variant="secondary">Default</Badge>
          </div>
        ) : (
          <p className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
            No saved payment method.
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          Card details are handled by the payment provider. Zworks never sees the number.
        </p>
      </section>

      <section aria-labelledby="billing-details-title" className="space-y-3">
        <div>
          <h2 id="billing-details-title" className="text-lg font-semibold tracking-tight">
            Billing details
          </h2>
          <p className="text-sm text-muted-foreground">
            Receipts and invoices go to this email address.
          </p>
        </div>
        <label htmlFor="billing-email" className="block text-sm font-medium">
          Billing email
        </label>
        <Input
          id="billing-email"
          aria-label="Billing email"
          type="email"
          autoComplete="email"
          value={form.billingEmail ?? ""}
          disabled={mutation.isPending}
          onChange={(event) => edit({ billingEmail: event.target.value || null })}
        />
        <label htmlFor="billing-tax-id" className="block text-sm font-medium">
          Company tax ID
        </label>
        <Input
          id="billing-tax-id"
          aria-label="Company tax ID"
          value={form.taxId ?? ""}
          maxLength={80}
          disabled={mutation.isPending}
          onChange={(event) => edit({ taxId: event.target.value || null })}
        />
        <label htmlFor="billing-tax-rate" className="block text-sm font-medium">
          VAT rate (%)
        </label>
        <Input
          id="billing-tax-rate"
          aria-label="VAT rate percent"
          type="number"
          min={0}
          max={100}
          step={0.1}
          className="w-36 tabular-nums"
          value={form.taxRate == null ? "" : String(form.taxRate * 100)}
          disabled={mutation.isPending}
          onChange={(event) =>
            edit({
              taxRate: event.target.value === "" ? null : Number(event.target.value) / 100,
            })
          }
        />
        <p className="text-sm text-muted-foreground">
          Invoices split the charged amount into net and VAT. Leave empty for no VAT.
        </p>
      </section>

      <section aria-labelledby="billing-invoices-title" className="space-y-3">
        <h2 id="billing-invoices-title" className="text-lg font-semibold tracking-tight">
          Invoices
        </h2>
        {invoices.length === 0 ? (
          <EmptyState title="No invoices yet" description="Confirmed payments will appear here." />
        ) : (
          <div className="divide-y rounded-lg border">
            {invoices.map((invoice) => (
              <article
                key={invoice.id}
                aria-label={`Invoice ${invoice.number}`}
                className="flex flex-wrap items-center justify-between gap-3 p-3"
              >
                <div>
                  <p className="font-medium">
                    {formatDate(invoice.issuedAt)} · {formatCredits(invoice.credits)} credits
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatCentsUsd(invoice.totalCents)} including tax · {invoice.number}
                  </p>
                </div>
                <Badge variant={invoice.status === "issued" ? "secondary" : "warning"}>
                  {invoice.status === "issued" ? "Issued" : "Void"}
                </Badge>
              </article>
            ))}
          </div>
        )}
      </section>

      {mutation.isError ? (
        <p role="alert" className="text-sm text-danger">
          {mutation.error instanceof ApiError
            ? (mutation.error.problem?.detail ?? mutation.error.message)
            : "Billing settings could not be saved."}
        </p>
      ) : null}
      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button type="submit" disabled={!hasChanged || mutation.isPending}>
          {mutation.isPending ? "Saving…" : "Save billing settings"}
        </Button>
      </div>
    </form>
  );
}
